import { useFirebase } from 'contexts/FirebaseContext';
import dayjs from 'dayjs';
import {
  DocumentReference,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useUser } from 'hooks/useUser';
import { LocalTrack } from 'models/LocalTrack';
import { Requester } from 'models/Requester';
import { Track } from 'models/Track';
import {
  TrackRequest,
  TrackRequestMessage,
  TrackRequestPriority,
  TrackRequestType,
  TrackRequesterPriorityValue,
} from 'models/TrackRequest';
import { useRef, useState } from 'react';
import { cleanArtistTitle } from 'utils/cleanArtistTitle';
import { isElectron } from 'utils/isElectron';
import { requestHash } from 'utils/requestHash';
import { useLocalDatabase } from './useLocalDatabase';
import { useTrackMapping } from './useTrackMapping';

const FIRESTORE_BATCH_LIMIT = 500;

const sortRequests = (requestsList: TrackRequest[]): TrackRequest[] => {
  return [...requestsList].sort((a, b) => {
    const aTime = a.date ? new Date(a.date).getTime() : 0;
    const bTime = b.date ? new Date(b.date).getTime() : 0;

    // If dates are different (more than 1 second apart), sort by date descending
    if (Math.abs(aTime - bTime) > 1000) {
      return bTime - aTime;
    }

    // Within the same second (e.g. batch imported Spotify playlist), sort by index ascending
    if (a.index !== undefined && b.index !== undefined) {
      return a.index - b.index;
    }

    if (a.index !== undefined) return -1;
    if (b.index !== undefined) return 1;

    return bTime - aTime;
  });
};

export function useRequests(userId?: string) {
  const [requests, setRequests] = useState<TrackRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isSubmittingRequests, setIsSubmittingRequests] = useState(false);
  const knownRequestsByHashRef = useRef<Map<string, TrackRequest>>(new Map());
  const fetchPromiseRef = useRef<Promise<TrackRequest[]> | null>(null);
  const { localDatabase } = useLocalDatabase();
  const db = useFirebase().db;
  const { getMappedTrack, getMappingAutoMatchThresholdValue, getFindMatchThreshold } =
    useTrackMapping();
  const { requesterId, preferences } = useUser();

  const findLocalTracks = async (request: TrackRequest<Track>, maxDistance = 1.0, limit = 20) => {
    if (isElectron() && request.type === TrackRequestType.Track) {
      // search with original parameters
      //const result = await localDatabase?.findTracks(request.value.title, request.value.artist,null,null,maxDistance,limit);
      const result = await localDatabase?.findTracks({
        title: request.value.title,
        artist: request.value.artist,
        maxDistance,
        limit,
      });

      if (result) {
        return result;
      }

      // research with cleaned parameters
      const { artist, title } = cleanArtistTitle(request.value);
      //return localDatabase?.findTracks(title, artist,null,null,null);
      return await localDatabase?.findTracks({
        title: title,
        artist: artist,
        maxDistance,
        limit,
      });
    }
  };

  const processRequestMapping = async (
    request: TrackRequest,
    localDatabase: any,
    getMappedTrack: any
  ): Promise<TrackRequest> => {
    if (isElectron()) {
      if (request.localId != null) {
        request.localTrack = await localDatabase?.getTrackById(request.localId);
      } else {
        const threshold = getMappingAutoMatchThresholdValue(preferences.autoMatchThreshold);
        const findMatchThreshold = getFindMatchThreshold();
        const localTrack = request.skipAutoMap ? null : await getMappedTrack(request);

        if (localTrack) {
          request.localId = localTrack?.id;
          request.localTrack = localTrack;
        } else {
          request.foundLocalTracks = await findLocalTracks(
            request as TrackRequest<Track>,
            findMatchThreshold
          );

          if (null != request.foundLocalTracks) {
            var trackRequest = request as TrackRequest<Track>;

            const logRequestName =
              request.type === TrackRequestType.Track
                ? `${trackRequest.value.artist} - ${trackRequest.value.title}`
                : `${request.value}`;

            const distance = request?.foundLocalTracks[0]?.distance;

            if (!request.skipAutoMap && distance && distance <= threshold) {
              console.log(
                `${logRequestName}: ${request.foundLocalTracks.length} found, best distance: ${distance}, threshold: ${threshold}`
              );
              request.localTrack = request.foundLocalTracks[0];
              request.localId = request.foundLocalTracks[0].id;
              request.autoMapped = true;
            } else {
              request.localTrack = undefined;
              request.localId = undefined;
              request.autoMapped = false;

              if (distance) {
                console.log(`${logRequestName}: Distance ${distance}, threshold: ${threshold}`);
              } else {
                console.log(`${logRequestName}: Distance is undefined`);
              }
            }
          }
        }
      }
    }

    return request;
  };

  const fetchRequests = async (eventId: string) => {
    // console.trace('fetchRequests called');
    if (!userId) {
      return [];
    }

    setIsLoadingRequests(true);

    const doFetch = async () => {
      try {
        const requestsCollection = collection(db, `user/${userId}/events/${eventId}/requests`);
        const requestsSnapshot = await getDocs(requestsCollection);

        const res = await Promise.all(
          requestsSnapshot.docs.map(async (requestDoc) => {
            // Process mapping
            const processedRequest = await processRequestMapping(
              requestDoc.data() as TrackRequest,
              localDatabase,
              getMappedTrack
            );

            const request = {
              ...processedRequest,
              id: requestDoc.id,
            };

            const requestersCollection = collection(
              db,
              `user/${userId}/events/${eventId}/requests/${request.id}/requesters`
            );
            const messagesCollection = collection(
              db,
              `user/${userId}/events/${eventId}/requests/${request.id}/messages`
            );
            const prioritiesCollection = collection(
              db,
              `user/${userId}/events/${eventId}/requests/${request.id}/priorities`
            );

            const [requestersSnapshot, messagesSnapshot, prioritiesSnapshot] = await Promise.all([
              getDocs(requestersCollection),
              getDocs(messagesCollection),
              getDocs(prioritiesCollection),
            ]);

            request.requesters = requestersSnapshot.docs.map((requesterDoc) => {
              const requester = requesterDoc.data() as Requester;

              return {
                ...requester,
                id: requesterDoc.id,
              };
            });

            request.messages = messagesSnapshot.docs.map((messageDoc) => {
              const message = messageDoc.data() as TrackRequestMessage;

              return {
                ...message,
                id: messageDoc.id,
              };
            });

            request.priorities = prioritiesSnapshot.docs.map((priorityDoc) => {
              const priority = priorityDoc.data() as TrackRequestPriority;

              return {
                ...priority,
                id: priorityDoc.id,
              };
            });

            return request;
          })
        );

        const nextKnownMap = new Map<string, TrackRequest>();
        res.forEach((r) => {
          if (r.hash) {
            nextKnownMap.set(r.hash, r);
          }
        });
        knownRequestsByHashRef.current = nextKnownMap;

        const sortedRequests = sortRequests(res);
        setRequests(sortedRequests);

        return sortedRequests;
      } finally {
        setIsLoadingRequests(false);
        fetchPromiseRef.current = null;
      }
    };

    const promise = doFetch();
    fetchPromiseRef.current = promise;
    return promise;
  };

  const updateRequests = async (eventId: string, updatedList: TrackRequest[]) => {
    if (!userId || !updatedList.length) {
      return;
    }

    const writeOps: Array<{ ref: DocumentReference; data: any }> = [];

    for (const updatedRequest of updatedList) {
      const requestId = updatedRequest.id;
      if (!requestId) continue;

      const docData: any = { ...updatedRequest };
      delete docData.id;
      delete docData.requesters;
      delete docData.messages;
      delete docData.priorities;
      delete docData.localTrack;
      delete docData.foundLocalTracks;

      // Delete fields that are undefined to avoid storing them in Firestore
      Object.keys(docData).forEach((key) => {
        if (docData[key] === undefined) {
          docData[key] = deleteField();
        }
      });

      writeOps.push({
        ref: doc(db, `user/${userId}/events/${eventId}/requests`, requestId),
        data: docData,
      });
    }

    const batchPromises: Promise<void>[] = [];
    for (let i = 0; i < writeOps.length; i += FIRESTORE_BATCH_LIMIT) {
      const batch = writeBatch(db);
      const chunk = writeOps.slice(i, i + FIRESTORE_BATCH_LIMIT);
      chunk.forEach((op) => batch.set(op.ref, op.data, { merge: true }));
      batchPromises.push(batch.commit());
    }

    if (batchPromises.length > 0) {
      await Promise.all(batchPromises);
    }

    setRequests((prevRequests) =>
      sortRequests(
        prevRequests.map(
          (req) => updatedList.find((updatedReq) => updatedReq.id === req.id) || req
        )
      )
    );
  };

  const addAnonymousRequests = async (eventId: string, requestTracks: TrackRequest[]) => {
    if (!userId || !requesterId || !requestTracks.length) {
      return;
    }

    setIsSubmittingRequests(true);

    try {
      // Wait for any in-flight initial fetch so our in-memory cache of existing requests is complete
      if (fetchPromiseRef.current) {
        await fetchPromiseRef.current;
      }

      const requestsCollection = collection(db, `/user/${userId}/events/${eventId}/requests`);
      const importDate = dayjs().toISOString();

      // Sort requestTracks by index ascending and deduplicate by hash
      const sortedTracks = [...requestTracks].sort((a, b) => {
        const idx1 = a.index !== undefined ? a.index : 0;
        const idx2 = b.index !== undefined ? b.index : 0;
        return idx1 - idx2;
      });

      const uniqueTracksMap = new Map<string, TrackRequest>();
      for (const track of sortedTracks) {
        if (track.hash && !uniqueTracksMap.has(track.hash)) {
          uniqueTracksMap.set(track.hash, track);
        }
      }
      const uniqueRequestTracks = Array.from(uniqueTracksMap.values());

      // Lookup map from in-memory cache (0 Firestore reads required)
      const existingByHash = new Map<string, TrackRequest>();
      knownRequestsByHashRef.current.forEach((req, hash) => existingByHash.set(hash, req));
      requests.forEach((req) => {
        if (req.hash) {
          existingByHash.set(req.hash, req);
        }
      });

      const writeOps: Array<{ ref: DocumentReference; data: any }> = [];
      const updatedRequests: TrackRequest[] = [];

      for (const incomingTrack of uniqueRequestTracks) {
        const existing = existingByHash.get(incomingTrack.hash);

        // Skip if already requested by the current user (0 reads, 0 writes)
        const alreadyRequestedByMe = existing?.requesters?.some(({ id }) => id === requesterId);
        if (alreadyRequestedByMe && existing) {
          updatedRequests.push(existing);
          continue;
        }

        const requesterData: Requester = {
          date: importDate,
        };

        let requestId = existing?.id || incomingTrack.id;
        let baseRequest: TrackRequest;

        if (!requestId) {
          // Generate new request doc ref locally without network roundtrip
          const newRequestRef = doc(requestsCollection);
          requestId = newRequestRef.id;

          const requestDate = incomingTrack.date || importDate;
          const requestDocData: TrackRequest = {
            ...incomingTrack,
            date: requestDate,
          };
          delete requestDocData.id;
          delete requestDocData.requesters;
          delete requestDocData.messages;
          delete requestDocData.priorities;
          delete requestDocData.localTrack;
          delete requestDocData.foundLocalTracks;

          writeOps.push({
            ref: newRequestRef,
            data: requestDocData,
          });

          baseRequest = {
            ...incomingTrack,
            id: requestId,
            date: requestDate,
            requesters: [],
          };
        } else {
          baseRequest = {
            ...(existing || incomingTrack),
            id: requestId,
            playlistInfo: incomingTrack.playlistInfo || existing?.playlistInfo,
          };
        }

        const requesterDocRef = doc(
          db,
          `/user/${userId}/events/${eventId}/requests/${requestId}/requesters`,
          requesterId
        );

        writeOps.push({
          ref: requesterDocRef,
          data: requesterData,
        });

        const updatedTrack: TrackRequest = {
          ...baseRequest,
          requesters: [
            ...(baseRequest.requesters || []),
            { ...requesterData, id: requesterId },
          ],
        };

        knownRequestsByHashRef.current.set(updatedTrack.hash, updatedTrack);
        existingByHash.set(updatedTrack.hash, updatedTrack);
        updatedRequests.push(updatedTrack);
      }

      // Commit all writes in Firestore batches (max 500 operations per batch)
      const batchPromises: Promise<void>[] = [];
      for (let i = 0; i < writeOps.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = writeBatch(db);
        const chunk = writeOps.slice(i, i + FIRESTORE_BATCH_LIMIT);
        for (const op of chunk) {
          batch.set(op.ref, op.data);
        }
        batchPromises.push(batch.commit());
      }

      if (batchPromises.length > 0) {
        await Promise.all(batchPromises);
      }

      if (updatedRequests.length > 0) {
        setRequests((prevRequests) => {
          const existingMap = new Map(prevRequests.map((req) => [req.hash, req]));
          updatedRequests.forEach((req) => existingMap.set(req.hash, req));
          return sortRequests(Array.from(existingMap.values()));
        });
      }
    } finally {
      setIsSubmittingRequests(false);
    }
  };

  const removeAnonymousRequest = async (eventId: string, request: TrackRequest) => {
    let requestId =
      request.id ||
      knownRequestsByHashRef.current.get(request.hash)?.id ||
      requests.find((r) => r.hash === request.hash)?.id;

    // Fallback to Firestore query only if ID is not known locally
    if (!requestId) {
      const requestsCollection = collection(db, `/user/${userId}/events/${eventId}/requests`);
      const q = query(requestsCollection, where('hash', '==', request.hash));
      const requestSnapshot = await getDocs(q);

      if (!requestSnapshot.empty) {
        requestId = requestSnapshot.docs[0].id;
      }
    }

    // Can not remove requests without an ID
    if (!requestId) {
      return;
    }

    const foundRequest = requests.find(
      (req) =>
        req.id === requestId && req.requesters?.some(({ id }) => id === requesterId)
    );

    if (foundRequest) {
      const updatedRequest: TrackRequest = {
        ...foundRequest,
        requesters: foundRequest.requesters?.filter(({ id }) => id !== requesterId),
      };

      const requesterDocRef = doc(
        db,
        `/user/${userId}/events/${eventId}/requests/${requestId}/requesters/${requesterId}`
      );

      try {
        await deleteDoc(requesterDocRef);

        knownRequestsByHashRef.current.set(updatedRequest.hash, updatedRequest);

        setRequests((prevRequests) =>
          sortRequests(
            prevRequests.map((req) => (updatedRequest.hash === req.hash ? updatedRequest : req))
          )
        );

        await removeRequestPriority(eventId, updatedRequest);
      } catch (error) {
        console.error('Error removing requester document: ', error);
      }
    }
  };

  const addRequestMapping = async (
    eventId: string,
    request: TrackRequest,
    localTrack: LocalTrack,
    rememberSelection: boolean
  ) => {
    const mappingId =
      request.type == TrackRequestType.Track
        ? await requestHash((request.value as Track).title, (request.value as Track).artist)
        : await requestHash(request.value as string);

    const mappingRef = localDatabase?.getMapping(mappingId);

    if (rememberSelection) {
      if (mappingRef === undefined) {
        localDatabase?.addMapping(mappingId, localTrack.id, localTrack.path);
      } else {
        localDatabase?.updateMapping(mappingId, localTrack.id, localTrack.path);
      }
    }

    updateRequests(eventId, [
      {
        ...request,
        localId: localTrack.id,
        localTrack: localTrack,
        autoMapped: false,
        skipAutoMap: false,
      },
    ]);
  };

  const removeRequestMapping = async (
    eventId: string,
    request: TrackRequest,
    rememberSelection: boolean
  ) => {
    const mappingId =
      request.type == TrackRequestType.Track
        ? await requestHash((request.value as Track).title, (request.value as Track).artist)
        : await requestHash(request.value as string);

    const mappingRef = localDatabase?.getMapping(mappingId);

    if (rememberSelection && mappingRef !== undefined) {
      localDatabase?.removeMapping(mappingId);
    }

    updateRequests(eventId, [
      {
        ...request,
        localId: undefined,
        localTrack: undefined,
        autoMapped: false,
        skipAutoMap: true,
      },
    ]);
  };

  const updateRequestMessage = async (message: string, eventId: string, request: TrackRequest) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const messageData: TrackRequestMessage = {
      value: message,
      date: new Date().toISOString(),
    };

    await setDoc(
      doc(db, `/user/${userId}/events/${eventId}/requests/${request.id}/messages`, requesterId),
      messageData
    );

    setRequests((prevRequests) =>
      prevRequests.map((req) => {
        if (req.hash !== request.hash) {
          return req;
        }

        const existingMessages = req.messages || [];
        const filteredMessages = existingMessages.filter((message) => message.id !== requesterId);

        return {
          ...req,
          messages: [...filteredMessages, { ...messageData, id: requesterId }],
        };
      })
    );
  };

  const removeRequestMessage = async (eventId: string, request: TrackRequest) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const messageDocRef = doc(
      db,
      `/user/${userId}/events/${eventId}/requests/${request.id}/messages/${requesterId}`
    );

    try {
      await deleteDoc(messageDocRef);

      setRequests((prevRequests) => {
        return prevRequests.map((req) => {
          if (req.hash !== request.hash) {
            return req;
          }

          const existingMessages = req.messages || [];
          const filteredMessages = existingMessages.filter(
            (priority) => priority.id !== requesterId
          );

          return {
            ...req,
            messages: filteredMessages,
          };
        });
      });
    } catch (error) {
      console.error('Error removing priority document: ', error);
    }
  };

  const updateRequestPriority = async (
    requestPriorityValue: TrackRequesterPriorityValue,
    eventId: string,
    request: TrackRequest
  ) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const priorityData: TrackRequestPriority = {
      value: requestPriorityValue,
    };

    await setDoc(
      doc(db, `/user/${userId}/events/${eventId}/requests/${request.id}/priorities`, requesterId),
      priorityData
    );

    if (
      requestPriorityValue === TrackRequesterPriorityValue.High ||
      requestPriorityValue === TrackRequesterPriorityValue.Normal
    ) {
      await addAnonymousRequests(eventId, [request]);
    } else if (requestPriorityValue === TrackRequesterPriorityValue.Low) {
      await removeAnonymousRequest(eventId, request);
    }

    setRequests((prevRequests) =>
      prevRequests.map((req) => {
        if (req.hash !== request.hash) {
          return req;
        }

        const existingPriorities = req.priorities || [];
        const filteredPriorities = existingPriorities.filter(
          (priority) => priority.id !== requesterId
        );

        return {
          ...req,
          priorities: [...filteredPriorities, { ...priorityData, id: requesterId }],
        };
      })
    );
  };

  const removeRequestPriority = async (eventId: string, request: TrackRequest) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const priorityDocRef = doc(
      db,
      `/user/${userId}/events/${eventId}/requests/${request.id}/priorities/${requesterId}`
    );

    try {
      await deleteDoc(priorityDocRef);

      setRequests((prevRequests) => {
        return prevRequests.map((req) => {
          if (req.hash !== request.hash) {
            return req;
          }

          const existingPriorities = req.priorities || [];
          const filteredPriorities = existingPriorities.filter(
            (priority) => priority.id !== requesterId
          );

          return {
            ...req,
            priorities: filteredPriorities,
          };
        });
      });
    } catch (error) {
      console.error('Error removing priority document: ', error);
    }
  };

  return {
    requests,
    validRequests: requests.filter((r) => !r.isDeleted),
    deletedRequests: requests.filter((r) => r.isDeleted === true),
    isLoadingRequests,
    isSubmittingRequests,
    fetchRequests,
    updateRequests,
    addAnonymousRequests,
    removeAnonymousRequest,
    findLocalTracks,
    processRequestMapping,
    addRequestMapping,
    removeRequestMapping,
    updateRequestMessage,
    removeRequestMessage,
    updateRequestPriority,
    removeRequestPriority,
  };
}
